import {
  AnchorButton,
  Button,
  Card,
  CardButton,
  FloatingTooltip,
  TooltipCore,
} from '@payaca/ambrosia';
import UntitledIcon from '@payaca/untitled-icons';
import { clstx } from '@payaca/utilities/cvaMerge';
import { formatDistanceToNow } from 'date-fns';
import { FC } from 'react';
import { Link } from 'react-router-dom';

import { InboundEmailInboxQuery } from '@/gql/graphql';

type InboxEmail =
  InboundEmailInboxQuery['me']['inboundEmailInbox']['items'][number];

export type EmailCardProps = {
  email: InboxEmail;
  isSelected: boolean;
  onSelect: () => void;
  onArchive?: () => void;
  onUnarchive?: () => void;
};

const EmailCard: FC<EmailCardProps> = (props) => {
  const { email, isSelected, onSelect, onArchive, onUnarchive } = props;

  const isUnread = email.status === 'UNREAD';

  const isArchived = email.status === 'ARCHIVED';
  const shouldShowUnreadDot = isUnread;

  const isForwardedEmail = email.addressType === 'FORWARD';

  return (
    <CardButton
      sizeVariant={'xs'}
      type="button"
      className={clstx(
        'group flex w-full cursor-pointer flex-col gap-1.5 border-b p-4 text-left transition-colors',
        isSelected && 'ring-2 ring-inset ring-blue-600'
      )}
      onClick={onSelect}
    >
      <Card.Body>
        <div className="flex items-start justify-between gap-2">
          <div className="flex min-w-0 items-center gap-2">
            {isArchived ? (
              <UntitledIcon name="box.3" className="size-4 shrink-0" />
            ) : (
              shouldShowUnreadDot && (
                <UntitledIcon
                  name="circle.4"
                  className="size-4 text-yellow-600 shrink-0"
                />
              )
            )}

            <h4 className="truncate">
              {isForwardedEmail
                ? `Fwd: ${email.originalFrom ?? email.fromEmail}`
                : `To: ${email.toEmails[0]}`}
            </h4>
          </div>
          <div className="flex shrink-0 items-center">
            <p
              className={clstx(
                'supporting-body whitespace-nowrap text-gray-500',
                (!!onArchive || !!onUnarchive) && 'group-hover:hidden'
              )}
            >
              {formatDistanceToNow(new Date(email.createdAt), {
                addSuffix: true,
              })}
            </p>
            {!!onArchive && (
              <FloatingTooltip>
                <Button
                  variant="white"
                  size="xs"
                  colour="black"
                  className="hidden group-hover:inline-flex"
                  onClick={(e) => {
                    e.stopPropagation();
                    onArchive();
                  }}
                  aria-label="Archive email"
                >
                  <UntitledIcon name="box.3" className="size-4" />
                </Button>

                <TooltipCore>Archive</TooltipCore>
              </FloatingTooltip>
            )}
            {!!onUnarchive && (
              <FloatingTooltip>
                <Button
                  variant="white"
                  size="xs"
                  colour="black"
                  className="hidden group-hover:inline-flex"
                  onClick={(e) => {
                    e.stopPropagation();
                    onUnarchive();
                  }}
                  aria-label="Unarchive email"
                >
                  <UntitledIcon name="box.3" className="size-4" />
                </Button>

                <TooltipCore>Unarchive</TooltipCore>
              </FloatingTooltip>
            )}
          </div>
        </div>

        <div className="flex justify-between gap-2">
          <p className="supporting-body truncate text-gray-500">
            {email.subject}
          </p>

          {email.assignedProject ? (
            <AnchorButton
              as={Link}
              to={`/projects/${email.assignedProject.id}`}
              variant="link-inline"
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
            >
              #{email.assignedProject.reference}
            </AnchorButton>
          ) : (
            <span className="supporting-body text-gray-500">Unassigned</span>
          )}
        </div>
      </Card.Body>
    </CardButton>
  );
};

export default EmailCard;
